import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactModal from 'react-modal';
import axios from 'axios';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { useLanguage } from '../../hooks/LanguageContext';
import translations from '../../utils/translations';
import { isApp } from '../../utils/isApp';
import LazySpinner from './LazySpinner';
import GuardChildren, { isGuardChildError } from './GuardChildren';
import styles from '../../styles/components/TermsPopup.module.css';

/**
 * 미동의 약관 관문.
 *
 * NoticePopup(mode='required')의 껍데기(모달 설정·children 보류·fail-open)를 따르되,
 * 조회/판정/저장은 약관 도메인이다. 공지 관문보다 앞에서 판정한다(ProviderRouter).
 *
 * 흐름: GET /profile → 미동의면 팝업, 동의 완료면 children 통과.
 *  · 판정: (terms_service_yn='Y' AND terms_individual_yn='Y')
 *          OR (agreement_app_yn='Y' AND privacy_app_yn='Y')
 *    약관 문서가 웹·앱 공통이라 어느 세트든 유효한 동의로 본다.
 *  · 저장: isApp() 으로 한 세트만 보낸다(서버 CONSENT_COLUMNS 가 나머지를 건드리지 않는다).
 *
 * ★비로그인(401)·조회 실패는 통과시킨다(fail-open) — 관문이 로그인 자체를 막으면 안 된다.
 */

/** 약관 전문 라우트(마켓 문서). public/legal/*.html 과 같은 내용을 React 라우트로 옮긴 것이다.
 *  ★두 라우트는 TermsGuard/RequiredNoticeGuard 면제 대상이어야 한다 — 미동의 상태에서 여는
 *   문서라 면제하지 않으면 전문 대신 이 팝업이 다시 뜬다.
 *  ★public/legal/*.html 은 남겨둔다 — 앱 드로어(naegift_mobile_app config.dart:119-120)가 직접 참조. */
const AGREEMENT_URL = '/naegift-app-terms';
const PRIVACY_URL = '/naegift-app-privacy';

type Phase = 'loading' | 'consent' | 'done';

interface TermsPopupProps {
  children?: React.ReactNode;
}

/** GET /profile 중 판정에 쓰는 필드만. 나머지 필드는 무시한다. */
interface ConsentProfile {
  termsServiceYn?: string | null;
  termsIndividualYn?: string | null;
  agreementAppYn?: string | null;
  privacyAppYn?: string | null;
}

function hasRequiredConsent(p: ConsentProfile | null): boolean {
  if (!p) return false;
  const web = p.termsServiceYn === 'Y' && p.termsIndividualYn === 'Y';
  const app = p.agreementAppYn === 'Y' && p.privacyAppYn === 'Y';
  return web || app;
}

/**
 * 오버레이/컨테이너는 인라인 — NoticePopup.tsx MODAL_STYLE 과 동일 값.
 * ★zIndex: MainHeader(sticky)·채널필터 위로 확실히 올린다.
 */
const MODAL_STYLE = {
  overlay: {
    background: 'rgba(34, 34, 34, 0.5)',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: '12px',
    zIndex: 1000,
  },
  content: {
    inset: 'unset' as const,
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    maxWidth: '420px',
    // ★뷰포트 기준 — 웹뷰 높이가 짧아도 팝업이 화면을 넘지 않는다(넘치는 건 목록이 스크롤로 흡수).
    maxHeight: '75vh',
    padding: '18px',
    borderRadius: '10px',
    overflow: 'hidden' as const,
  },
};

// [2026-11-01] 조회가 끝날 때까지 하위 화면을 보류하므로 API 가 멈추면 화면 전체가 스피너에 묶인다 —
//  이 시간이 지나면 조회 실패와 똑같이 통과시킨다.
const GUARD_FETCH_TIMEOUT_MS = 5000;

function TermsPopup({ children }: TermsPopupProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];

  const [phase, setPhase] = useState<Phase>('loading');
  const [busy, setBusy] = useState(false);
  const [agreements, setAgreements] = useState({
    service: false, // 필수
    privacy: false, // 필수
    marketing: false, // 선택
  });

  // 마운트 1회 조회. useGlobalAxiosFetch 가 아니라 axios 직접 호출이다 —
  //  그 훅은 401 에서 /login 으로 보내는데(useGlobalAxiosFetch.ts:64-70) 관문은 통과시켜야 한다.
  useEffect(() => {
    let cancelled = false;

    const loadProfile = async (): Promise<void> => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_SERVER}/profile`,
          { withCredentials: true, timeout: GUARD_FETCH_TIMEOUT_MS }
        );
        if (cancelled) return;
        // ResponseInterceptor 로 한 겹 감싸여 온다: { resultCode, resultMessage, data }
        const data = (res?.data?.data ?? res?.data) as ConsentProfile;
        setPhase(hasRequiredConsent(data) ? 'done' : 'consent');
      } catch (e) {
        // 비로그인(401)·네트워크 실패 모두 통과 — 관문이 페이지를 막지 않는다.
        if (!cancelled) setPhase('done');
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const allChecked = useMemo(
    () => agreements.service && agreements.privacy && agreements.marketing,
    [agreements]
  );
  const requiredChecked = useMemo(
    () => agreements.service && agreements.privacy,
    [agreements]
  );

  const toggleAll = useCallback(() => {
    // 전체 동의는 선택 항목까지 포함한다(StoreConsent.js:44-50 패턴).
    const next = !allChecked;
    setAgreements({ service: next, privacy: next, marketing: next });
  }, [allChecked]);

  const toggle = useCallback((key: 'service' | 'privacy' | 'marketing') => {
    setAgreements(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleAgree = useCallback(async (): Promise<void> => {
    if (!requiredChecked || busy) return;
    setBusy(true);
    try {
      const marketing = agreements.marketing ? 'Y' : 'N';
      // isApp() 으로 한 세트만 보낸다 — 서버는 넘어온 필드만 갱신한다.
      const body = isApp()
        ? {
            agreementAppYn: 'Y',
            privacyAppYn: 'Y',
            marketingAppYn: marketing,
          }
        : {
            termsServiceYn: 'Y',
            termsIndividualYn: 'Y',
            termsMarketingYn: marketing,
          };
      await axios.patch(
        `${process.env.REACT_APP_API_SERVER}/consent`,
        body,
        { withCredentials: true }
      );
      setPhase('done');
    } catch (e) {
      // ★통과시키지 않는다 — 저장 실패는 미동의와 같다.
      console.error('[terms-gate] consent save failed:', e);
      alert(t.termsSaveFailed);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredChecked, busy, agreements.marketing, t]);

  /**
   * 관문 [닫기] — 로그아웃하지 않고 마켓홈으로 보낸다(미동의 = 로그아웃 아님).
   * replace: 뒤로가기로 관문에 재진입해 닫기 루프가 되는 것을 막는다(NoticePopup.tsx:302-304 동일).
   */
  const handleClose = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  /**
   * 배경 스크롤·터치 잠금.
   * ★훅이라 아래 조기 return 보다 반드시 위에서 호출해야 한다(훅 순서 규칙).
   * ★bodyRef 를 넘겨 팝업 목록 안에서 난 터치만 통과시킨다.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock(phase === 'consent', bodyRef);

  if (phase === 'loading') {
    return <LazySpinner />;
  }
  if (phase === 'done') {
    return <>{children}</>;
  }

  return (
    <ReactModal
      isOpen
      style={MODAL_STYLE}
      // 약관은 항상 필수 — 동의 전에는 어떤 경로로도 닫히면 안 된다([닫기] 버튼만 허용).
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      contentLabel={t.termsPopupTitle}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>{t.termsPopupTitle}</h2>
      </div>

      <div className={styles.body} ref={bodyRef}>
        <div className={styles.allAgreeRow} onClick={toggleAll}>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={toggleAll}
            onClick={e => e.stopPropagation()}
            className={styles.checkbox}
          />
          <span className={styles.allAgreeText}>{t.allAgree}</span>
        </div>

        <div className={styles.divider} />

        <div className={styles.item}>
          <div className={styles.itemHeader}>
            <div className={styles.itemLeft} onClick={() => toggle('service')}>
              <input
                type="checkbox"
                checked={agreements.service}
                onChange={() => toggle('service')}
                onClick={e => e.stopPropagation()}
                className={styles.checkbox}
              />
              <span className={styles.itemTitle}>{t.termsServiceLabel}</span>
            </div>
            {/* 앱 웹뷰(Android)는 window.open 의 URL 을 앵커 href 로만 얻는다 — <a> 필수. */}
            <a
              className={styles.viewBtn}
              href={AGREEMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.termsView}
            </a>
          </div>
        </div>

        <div className={styles.item}>
          <div className={styles.itemHeader}>
            <div className={styles.itemLeft} onClick={() => toggle('privacy')}>
              <input
                type="checkbox"
                checked={agreements.privacy}
                onChange={() => toggle('privacy')}
                onClick={e => e.stopPropagation()}
                className={styles.checkbox}
              />
              <span className={styles.itemTitle}>{t.termsPrivacyLabel}</span>
            </div>
            <a
              className={styles.viewBtn}
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.termsView}
            </a>
          </div>
        </div>

        <div className={styles.item}>
          <div className={styles.itemHeader}>
            <div className={styles.itemLeft} onClick={() => toggle('marketing')}>
              <input
                type="checkbox"
                checked={agreements.marketing}
                onChange={() => toggle('marketing')}
                onClick={e => e.stopPropagation()}
                className={styles.checkbox}
              />
              <span className={styles.itemTitle}>{t.termsMarketingLabel}</span>
            </div>
          </div>
          <div className={styles.itemDesc}>{t.termsMarketingDesc}</div>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonSecondary}`}
          onClick={handleClose}
          disabled={busy}
        >
          {t.noticeClose}
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={handleAgree}
          disabled={busy || !requiredChecked}
        >
          {busy ? t.processing : t.noticeAgree}
        </button>
      </div>
    </ReactModal>
  );
}

/**
 * 팝업 렌더 예외가 페이지까지 언마운트시키지 않도록 격리한다.
 * (비동기 실패는 각 호출부 try/catch 가 이미 삼킨다 — 이건 렌더/훅 예외용 최후 방어선)
 */
interface BoundaryState {
  failed: boolean;
  childError?: unknown;
}

export default class TermsPopupBoundary extends React.Component<
  TermsPopupProps,
  BoundaryState
> {
  state: BoundaryState = { failed: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { failed: true, childError: isGuardChildError(error) ? error : undefined };
  }

  componentDidCatch(error: Error): void {
    console.log('[terms-popup]', error);
  }

  render(): React.ReactNode {
    // 하위 화면 오류는 삼키지 않고 라우터 오류 화면(error-page)으로 보낸다(GuardChildren.tsx).
    if (this.state.childError) throw this.state.childError;
    // ★fail-open: 관문에서 팝업이 터져도 페이지를 막지 않는다(백지 방지).
    const children = <GuardChildren>{this.props.children}</GuardChildren>;
    if (this.state.failed) return children;
    return <TermsPopup>{children}</TermsPopup>;
  }
}
